import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Package, 
  Send, 
  Eye, 
  Download, 
  Upload, 
  Award,
  BarChart3,
  TrendingUp,
  Activity
} from "lucide-react";

interface PackageStats {
  totalPackages: number;
  addendumPackages: number;
  draftPackages: number;
  sentPackages: number;
  viewedPackages: number;
  downloadedPackages: number;
  returnedPackages: number;
  awardedPackages: number;
}

interface ProgressRingProps {
  percentage: number;
  size: number;
  strokeWidth: number;
  color: string;
  label: string;
  icon: React.ReactNode;
  count: number;
  total: number;
}

function ProgressRing({ percentage, size, strokeWidth, color, label, icon, count, total }: ProgressRingProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = `${percentage * circumference / 100} ${circumference}`;

  return (
    <div className="flex flex-col items-center space-y-2">
      <div className="relative">
        <svg width={size} height={size} className="transform -rotate-90">
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
            fill="transparent"
          />
          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={strokeDasharray}
            strokeLinecap="round"
            className="transition-all duration-300 ease-in-out"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="text-lg font-bold text-gray-900">{Math.round(percentage)}%</div>
            <div className="text-xs text-gray-500">{count}/{total}</div>
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-1 text-xs font-medium text-gray-700">
        {icon}
        <span>{label}</span>
      </div>
    </div>
  );
}

export default function PackageAnalyticsChart() {
  // Use mock data for admin analytics
  const stats: PackageStats = {
    totalPackages: 127,
    addendumPackages: 15,
    draftPackages: 45,
    sentPackages: 38,
    viewedPackages: 25,
    downloadedPackages: 15,
    returnedPackages: 4,
    awardedPackages: 2
  };

  // Convert string values to numbers for calculations
  const totalPackages = Number(stats.totalPackages) || 0;
  const draftPackages = Number(stats.draftPackages) || 0;
  const sentPackages = Number(stats.sentPackages) || 0;
  const viewedPackages = Number(stats.viewedPackages) || 0;
  const downloadedPackages = Number(stats.downloadedPackages) || 0;
  const returnedPackages = Number(stats.returnedPackages) || 0;
  const awardedPackages = Number(stats.awardedPackages) || 0;

  const draftPercentage = totalPackages > 0 ? (draftPackages / totalPackages) * 100 : 0;
  const sentPercentage = totalPackages > 0 ? (sentPackages / totalPackages) * 100 : 0;
  const viewedPercentage = sentPackages > 0 ? (viewedPackages / sentPackages) * 100 : 0;
  const downloadedPercentage = sentPackages > 0 ? (downloadedPackages / sentPackages) * 100 : 0;
  const returnedPercentage = sentPackages > 0 ? (returnedPackages / sentPackages) * 100 : 0;
  const awardedPercentage = returnedPackages > 0 ? (awardedPackages / returnedPackages) * 100 : 0;

  return (
    <Card className="border-gray-200 shadow-sm h-[500px]">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center justify-between text-lg font-semibold text-gray-900">
          <div className="flex items-center">
            <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
            Package Workflow Analytics
          </div>
          <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-200 flex items-center">
            <TrendingUp className="w-3 h-3 mr-1" />
            {totalPackages} packages
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="h-[calc(100%-80px)]">
        <div className="space-y-6">
          {/* Top Row: Creation to Tendering */}
          <div className="grid grid-cols-3 gap-6">
            <ProgressRing
              percentage={draftPercentage}
              size={70}
              strokeWidth={6}
              color="#3b82f6"
              label="Draft Tender"
              icon={<Package className="w-3 h-3 text-blue-600" />}
              count={draftPackages}
              total={totalPackages}
            />
            
            <ProgressRing
              percentage={sentPercentage}
              size={70}
              strokeWidth={6}
              color="#10b981"
              label="Sent for Tender"
              icon={<Send className="w-3 h-3 text-green-600" />}
              count={sentPackages}
              total={totalPackages}
            />
            
            <ProgressRing
              percentage={viewedPercentage}
              size={70}
              strokeWidth={6}
              color="#8b5cf6"
              label="Viewed"
              icon={<Eye className="w-3 h-3 text-purple-600" />}
              count={viewedPackages}
              total={sentPackages}
            />
          </div>

          {/* Workflow Arrow */}
          <div className="flex justify-center">
            <div className="flex items-center space-x-2 text-gray-400">
              <div className="w-8 h-0.5 bg-gray-300"></div>
              <Activity className="w-4 h-4" />
              <div className="w-8 h-0.5 bg-gray-300"></div>
            </div>
          </div>

          {/* Bottom Row: Engagement to Completion */}
          <div className="grid grid-cols-3 gap-6">
            <ProgressRing
              percentage={downloadedPercentage}
              size={70}
              strokeWidth={6}
              color="#f59e0b"
              label="Downloaded"
              icon={<Download className="w-3 h-3 text-amber-600" />}
              count={downloadedPackages}
              total={sentPackages}
            />
            
            <ProgressRing
              percentage={returnedPercentage}
              size={70}
              strokeWidth={6}
              color="#ef4444"
              label="Returned"
              icon={<Upload className="w-3 h-3 text-red-600" />}
              count={returnedPackages}
              total={sentPackages}
            />
            
            <ProgressRing
              percentage={awardedPercentage}
              size={70}
              strokeWidth={6}
              color="#eab308"
              label="Awarded"
              icon={<Award className="w-3 h-3 text-yellow-600" />}
              count={awardedPackages}
              total={returnedPackages}
            />
          </div>

          {/* Summary Stats */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-lg font-semibold text-blue-600">{totalPackages}</div>
                <div className="text-xs text-gray-600">Total Packages</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-green-600">{Math.round(sentPercentage)}%</div>
                <div className="text-xs text-gray-600">Tender Rate</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-purple-600">{Math.round(returnedPercentage)}%</div>
                <div className="text-xs text-gray-600">Response Rate</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-yellow-600">{Math.round(awardedPercentage)}%</div>
                <div className="text-xs text-gray-600">Award Rate</div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}